import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { propertyService } from "../requests/propertyService";
import { requestService } from "../requests/requestService";
import { PropertyFactory } from "../factory/propertyFactory";
import { useModal } from "./useModal";
import { sharedData } from "../sharedData/sharedData";
import { propertyInfoFormValidator } from "../validations/validationRules/propertyInfoForm";
import { useValidatePropertyInfo } from "../validations/validationHooks/useValidatePropertyInfo";
import { AlreadyValidated, ValidatedSuccessfully, SavedSuccessfully, UpdatedSuccessfully, Saving, SkipSaving } from "../components/popups/popupMessages";
import { diligenceObservable } from "../observers/deligenceObservable";
import useDeligenceStatus from "./useDeligenceStatus";
import { handleAddressValidationNonUbo } from './addressValidator';
import { useValidateSingleAddress } from "../hooks/validateSingleAddress";
import { reloadAfterValidate } from "../utils/reloadAfterValidate";


export const usePropertyData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { modalOpen, modalMessage, setModalOpen, setModalMessage } = useModal();
  const [ modalHeading, setModalHeading ] = useState("");
  const [isPropertyCompleted, setIsPropertyCompleted] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const requestId = useMemo(()=>{return searchParams.get("requestId") || sharedData.get("requestId")}, [searchParams]);
  const transactionId = useMemo(()=>{return sharedData.get("transactionId")}, []);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [formData, setFormData] = useState(PropertyFactory({}));
  const [finalPropertyId, setFinalPropertyId] = useState(null);
  const [existingProperty, setExistingProperty] = useState([]);
  const ownerId = useMemo(()=>{ return "PropertyInfo"}, []);
  const { getDeligenceStatusByCountry } = useDeligenceStatus();

  const { validatePropertyInfo } = useValidatePropertyInfo();
const entityId = useMemo(()=>{return sharedData.get("entityId")},[]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false); // ✅ Add loading state

  const [isSelectorReadOnly, setIsSelectorReadOnly] = useState(searchParams.get("readonly") === "true");
  const [showPropertyConfirmModal, setShowPropertyConfirmModal] = useState(false);
  const [hasShownPropertyModal, setHasShownPropertyModal] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const isView = useMemo(() => searchParams.get("view") === "true", [searchParams]);
  const isEdit = useMemo(() => searchParams.get("edit") === "true", [searchParams]);

  const { validateSingleAddress } = useValidateSingleAddress();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [pendingUpdatedData, setPendingUpdatedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    const savedFormData = localStorage.getItem("propertyFormData");
    if (savedFormData) {
      setFormData(JSON.parse(savedFormData));
      setFinalPropertyId(JSON.parse(savedFormData).propertyId);
    }
    setIsDataLoaded(true); // Mark data as loaded after checking localStorage
  }, []);

  const checkIsReadOnly = useCallback((propertyDetails) => {
      const isEditMode = searchParams.get("edit") === "true";
      const isTransactionCompleted = propertyDetails.propertyStatus === "Validated";
      setIsPropertyCompleted(isTransactionCompleted);
      console.log(propertyDetails,isReadOnly)

      return isTransactionCompleted || (searchParams.get("readonly")==="true") || (searchParams.get("view")==="true");
    }, [isReadOnly, searchParams]);

  const fetchPropertyDetailsByPropertyId = useCallback(async (propertyId)=>{
      if (!propertyId) return;
      try {
        const propertyDetails = await propertyService.fetchPropertyDetailsByPropertyId(propertyId);
        if (propertyDetails) {
            const updatedFormData = PropertyFactory(propertyDetails);
            localStorage.setItem("propertyFormData", JSON.stringify(updatedFormData));
            sharedData.set("propertyId", updatedFormData.propertyId);
            setFormData(updatedFormData);
            setIsReadOnly(checkIsReadOnly(updatedFormData));
            setIsDisabled(checkIsReadOnly(updatedFormData));
        } else {
            console.warn("⚠️ No property details found in the response.");
        }
    } catch (error) {
        console.error("❌ Error fetching property details:", error);
    }
  },[checkIsReadOnly]);

  const fetchProperties = useCallback(async (requestId) => {
    try {
      const propertyIds = await propertyService.fetchPropertiesByRequestId(requestId);
      setExistingProperty(propertyIds);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  }, []);

  const fetchQuickPropertyDetailsByPropertyId = useCallback(async (propertyId)=>{
    if (!propertyId) return;
    try {
      const propertyDetails = await propertyService.fetchQuickPropertyDetailsByPropertyId(propertyId);
      if (propertyDetails) {
          const updatedFormData = PropertyFactory(propertyDetails);
          localStorage.setItem("propertyFormData", JSON.stringify(updatedFormData));
          sharedData.set("propertyId", updatedFormData.propertyId);
          setFormData(updatedFormData);
          setIsReadOnly(checkIsReadOnly(updatedFormData));
          setIsDisabled(checkIsReadOnly(updatedFormData));
      } else {
          console.warn("⚠️ No property details found in the response.");
      }
  } catch (error) {
      console.error("❌ Error fetching property details:", error);
  }
},[checkIsReadOnly]);



  useEffect(()=>{
    if (isView || isEdit) {
      fetchPropertyDetailsByPropertyId(sharedData.get("propertyId"));
    }
  },[fetchPropertyDetailsByPropertyId, isView, isEdit]);

  // Check if we should show the property confirmation modal
  useEffect(() => {
    // Wait until data is loaded before checking
    if (!isDataLoaded) return;

    const isViewMode = searchParams.get("view") === "true";
    const isReadOnlyMode = searchParams.get("readonly") === "true";

    // Check if property ID exists in formData or sharedData
    const sharedPropertyId = sharedData.get("propertyId");
    const hasPropertyId = (formData.propertyId !== undefined &&
                           formData.propertyId !== null &&
                           formData.propertyId !== "" &&
                           formData.propertyId !== "None" &&
                           formData.propertyId !== 0) ||
                          (sharedPropertyId !== undefined &&
                           sharedPropertyId !== null &&
                           sharedPropertyId !== "" &&
                           sharedPropertyId !== "None" &&
                           sharedPropertyId !== 0 &&
                           sharedPropertyId !== "N/A");

    console.log("🔍 Property Modal Check:", {
      isDataLoaded,
      isViewMode,
      isReadOnlyMode,
      hasPropertyId,
      propertyId: formData.propertyId,
      sharedPropertyId,
      propertyIdType: typeof formData.propertyId,
      hasShownPropertyModal,
      formDataKeys: Object.keys(formData)
    });

    // Only show modal if:
    // 1. Not in view mode
    // 2. Not in readonly mode
    // 3. No property ID exists (in formData or sharedData)
    // 4. Haven't shown the modal yet in this session
    if (!isViewMode && !isReadOnlyMode && !hasPropertyId && !hasShownPropertyModal) {
      console.log("✅ Showing property confirmation modal");
      setShowPropertyConfirmModal(true);
      setHasShownPropertyModal(true);
    }
  }, [formData.propertyId, searchParams, hasShownPropertyModal, isDataLoaded]);

  const showModal = useCallback((message) => {
    setModalMessage(message);
    setModalOpen(true);
  }, [setModalMessage, setModalOpen]);

  // Fetch properties if requestId Exists
  useEffect(() => {
    if (requestId) {
      fetchProperties(requestId);
    }else {
      console.error("No requestId found in location state");
    }
  }, [requestId, fetchProperties]);

  const handleSpecialCase = useCallback(async (name, value) => {
    if (name === "country") {
      diligenceObservable.remove(ownerId);
      if (value !== "None") {
        const response = await getDeligenceStatusByCountry(value);
        if (response) {
            const hasEnhanced = response.some((item) => item.Diligence.toLowerCase() === "enhanced");
            const hasStandard = response.some((item) => item.Diligence.toLowerCase() === "standard");
            const hasSimplified = response.some((item) => item.Diligence.toLowerCase() === "simplified");
            if (hasEnhanced) {
                diligenceObservable.setValue("Enhanced", ownerId);
            } else if (hasStandard) {
                diligenceObservable.setValue("Standard", ownerId);
            } else if (hasSimplified) {
                diligenceObservable.setValue("Simplified", ownerId);
            }
        }
      }
    }
  }, [getDeligenceStatusByCountry, ownerId]);

  const handleChange = (e) => {
    if (isDisabled) return;
    setHasUnsavedChanges(true);
    const updatedFormData = { ...formData, [e.target.name]: e.target.value };
    setFormData(updatedFormData);
    localStorage.setItem("propertyFormData", JSON.stringify(updatedFormData));
    handleSpecialCase(e.target.name, e.target.value);
  };

  const updateRequestTable = async () => {
    try {
      if (!requestId) {
        showModal("Request ID is missing. Cannot update request table.");
        return;
      }
      const propertyId = sharedData.get("propertyId");
      const transactionId = sharedData.get("transactionId");
     
      // BUG: Backend accept transaction id, when passing, throws 500
      const data = { requestId, propertyId, transactionId };
      console.log("Update request payload: ", data);
      await requestService.updateRequest(data);
    } catch (error) {
      showModal(` Failed to update request table: ${error.message}`);
    }
  };

  const handlePropertyChange = useCallback(async (e) => {
      setHasUnsavedChanges(true);
      const propertyId = e.target.value;
    
  
      if (propertyId !== "None" && propertyId !== "") {
        fetchQuickPropertyDetailsByPropertyId(propertyId); // ✅ Fetch property details by propertyId
      } else {
        // Reset form if no property is selected\

        const resetFormData = PropertyFactory({})

        // Save to localStorage
        localStorage.removeItem("propertyFormData");
        sharedData.delete("propertyId");
        // setIsReadOnly(false);
  
        // Update state
        setFormData(resetFormData);
        setIsDisabled(false);
        const isReadOnlyFromURL = searchParams.get("readonly") === "true" || searchParams.get("view") === "true";
        setIsReadOnly(isReadOnlyFromURL);
        setIsPropertyCompleted(false);
      }
    }, [fetchPropertyDetailsByPropertyId, searchParams]);


    
  const handleUpdate = async () => {
    setHasUnsavedChanges(false);

    setModalHeading("");
    setModalMessage(<Saving page={"Property Information"}/>);
    setModalOpen(true);
  
    // ✅ Step 0: If property is already completed, update request table directly
    if (formData.propertyStatus === "Validated") {
        console.log("⚠️ Property already completed. Skipping update...");
        setModalHeading("")
        setModalMessage( <SkipSaving page={"Property Information"} /> )
        console.log(requestId)
        await updateRequestTable(); // ✅ Call update request table API
      return; // ✅ Exit early since property is already completed
    }
  
    if (!requestId) {
      showModal("Request ID is missing. Cannot update property.");
      return;
    }

    setLoading(true); // ✅ Start loading state

    try {
      console.log("🔄 Updating Property with data:", PropertyFactory(formData));

      // ✅ Step 1: Update the property
      const updatedPropertyResponse = await propertyService.updateProperty(requestId, formData.propertyId, PropertyFactory(formData));

      if (!updatedPropertyResponse.ok) {
        showModal(`Failed to save property: ${updatedPropertyResponse.data?.error || "Unknown error"}`);
        return;
      }

      console.log("✅ Property updated successfully:", updatedPropertyResponse);
      const updatedProperty = updatedPropertyResponse.data;

      if (updatedProperty?.property_id) {
        fetchPropertyDetailsByPropertyId(updatedProperty.property_id);
        showModal("Property Detail Saved sucessfully");
        await evaluateAndSetDiligence(formData.country);
        sharedData.set('propertyId', updatedProperty.property_id);
        // ✅ Step 6: Update request table with new property ID after successful update
        await updateRequestTable();
      } else {
        showModal("Failed to save property: No property ID returned.");
      }
    } catch (error) {
      showModal(`Failed to update property: ${error.message || error.response?.data.message || "Unknown error"}`);
    } finally {
      setLoading(false); // ✅ Stop loading regardless of success or failure
    }
  };

  const evaluateAndSetDiligence = async (country) => {
    if (country && country !== "None") {
      const response = await getDeligenceStatusByCountry(country);
      if (response) {
        const levels = response.map(r => r.Diligence.toLowerCase());
        if (levels.includes("enhanced")) {
          diligenceObservable.setValue("Enhanced", ownerId);
        } else if (levels.includes("standard")) {
          diligenceObservable.setValue("Standard", ownerId);
        } else if (levels.includes("simplified")) {
          diligenceObservable.setValue("Simplified", ownerId);
        }
      }
    }
  };




  const validateFields = async () => {

    const errors = validatePropertyInfo(formData);
    if (errors) {
      setModalMessage(
        <>
          <strong>Please fill in the following required fields:</strong>
          <br />
          <div style={{ "maxHeight": "200px", "overflow": "auto" }}>
          {Object.keys(errors).map((field) => (
            <div key={field}>
              <ul>
                {errors[field].map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          ))}
          </div>
        </>
      );
      setModalOpen(true);
      return false;
    } else {
      return await handleAddressValidationNonUbo({
        formData,
        validateSingleAddress,
        setFormData,
        setModalOpen,
        setModalHeading,
        setModalMessage,
        setValidationResult,
        setPendingUpdatedData,
        setShowAddressModal
      });
    }
  }

  const handleValidate = async () => {

    showModal("Validating, please wait...");

    if (formData.propertyStatus === "Validated") {
      setModalHeading("Validation Result");
      showModal(<AlreadyValidated page="Property Information" />);
      await updateRequestTable();
      return
    }

    const { valid, data: validatedData } = await validateFields();
    if (!valid) return;
  
    // ✅ Prepare payload for API call
    const payload = {
      ...validatedData,
      propertyId: validatedData.propertyId, // Include propertyId
      propertyStatus: "Validated", 
      requestId:requestId
    };
  
    try {
      // ✅ Step 1: Update Property
      const updatedPropertyResponse = await propertyService.updateProperty(requestId, validatedData.propertyId, payload);
      console.log("✅ Property updated successfully:", updatedPropertyResponse);
      
      if (!updatedPropertyResponse.ok) {
        showModal(`Failed to validate property: ${updatedPropertyResponse.data?.error || "Unknown error"}`);
        return;
      }

      const updatedProperty = updatedPropertyResponse.data;

      if (updatedProperty?.property_id) {
        sharedData.set('propertyId', updatedProperty.property_id);
        fetchPropertyDetailsByPropertyId(updatedProperty.property_id);
        setHasUnsavedChanges(false);
        showModal(<ValidatedSuccessfully page="Property Information" />);
        await evaluateAndSetDiligence(validatedData.country);
        await updateRequestTable(); // ✅ Call update request table API
        reloadAfterValidate();
      } else {
        showModal("Failed to validate property: No property ID returned.");
      }

      
    } catch (error) {
      showModal(`Failed to validate property: ${error.message}`);
    }
  };

  const handleNext = async () => {
if (!hasUnsavedChanges){

  const newUrl = `/client-counter?${searchParams.toString()}`;

  // ✅ Step 3: Navigate to new page with state
  sharedData.set('propertyId', formData.propertyId);
  navigate(newUrl);

  return;



}

                  try {
                      // ✅ Step 1: Call handleSave before proceeding
                      await handleUpdate(); // ✅ Call handleSave first
                      console.log("Data saved successfully.");

                      // ✅ Step 2: Construct new URL with existing parameters
                      const newUrl = `/client-counter?${searchParams.toString()}`;

                      // ✅ Step 3: Navigate to new page with state
                      sharedData.set('propertyId', formData.propertyId);
                      navigate(newUrl);
                  } catch (error) {
                      console.error("❌ Error:", error);
                      showModal(`Failed to proceed to next page: ${error.response?.data?.message || error.message || "Unknown error"}`);
                  }
                }

  // Handle "Yes" - user has a property, stay on the page
  const handlePropertyConfirmYes = useCallback(() => {
    setShowPropertyConfirmModal(false);
    // User stays on the page to fill the form
  }, []);

  // Handle "No" - user doesn't have a property, redirect to next page
  const handlePropertyConfirmNo = useCallback(() => {
    setShowPropertyConfirmModal(false);
    // Navigate to the next page (client-counter)
    const newUrl = `/client-counter?${searchParams.toString()}`;
    navigate(newUrl);
  }, [navigate, searchParams]);

  return {
    isSelectorReadOnly,
    searchParams,
    requestId,
    transactionId,
    isPropertyCompleted,
    hasUnsavedChanges,
    isDisabled,
    isReadOnly,
    setIsReadOnly,
    formData,
    existingProperty,
    modalOpen,
    modalMessage,
    modalHeading,
    handleChange,
    setModalOpen,
    setFormData,
    handleValidate,
    handleUpdate,
    handlePropertyChange,
    handleNext,
    showAddressModal,
    validationResult,
    setShowAddressModal,
    pendingUpdatedData,
    entityId,
    showPropertyConfirmModal,
    handlePropertyConfirmYes,
    handlePropertyConfirmNo
  }
};
